import { useState, useEffect, useRef } from 'react';
import { API_BASE, apiFetch } from '../lib/api';
import MarkdownRenderer from './MarkdownRenderer';
import SummaryCard, { type DevResponseSummary } from './SummaryCard';

interface TokenUsage {
  inputTokens: number;
  outputTokens: number;
}

interface DevResponsePanelProps {
  projectId: string;
  sessionId: string;
  issueId?: string | null;
  onComplete: (artefactId: string | null, usage: TokenUsage) => void;
}

export default function DevResponsePanel({ projectId, sessionId, issueId, onComplete }: DevResponsePanelProps) {
  const [content, setContent] = useState('');
  const [tokenCount, setTokenCount] = useState(0);
  const [complete, setComplete] = useState(false);
  const [stopped, setStopped] = useState(false);
  const [error, setError] = useState('');
  // ACT.A4 (#1703) — set when the server responds 202 { status: 'queued',
  // position, estimated_wait_seconds } (per-tenant session load hardening).
  // Mirrors ChatPage.tsx's queueStatus; cleared once the auto-retry lands
  // (admitted or re-queued) or the user hits Stop.
  const [queueStatus, setQueueStatus] = useState<{ position: number; waitSeconds: number } | null>(null);
  const [artefactId, setArtefactId] = useState<string | null>(null);
  const [usage, setUsage] = useState<TokenUsage | null>(null);
  const [summary, setSummary] = useState<DevResponseSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  // Snapshot ref avoids re-triggering summary effect on every streaming token
  const contentSnapshotRef = useRef('');
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const controllerRef = useRef<AbortController | null>(null);
  const summaryRequestedRef = useRef(false);
  // ACT.A4 (#1703) — pending auto-retry timer scheduled after a 202
  // { status: 'queued' } response. Tracked so it can be cancelled: on
  // unmount (effect cleanup below) and via handleStop (the AbortController
  // is already-completed by the time this timer is pending, so it alone
  // can't cancel a queued retry). Mirrors ChatPage.tsx's
  // queueRetryTimeoutRef.
  const queueRetryTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('blueprint_token');
    const controller = new AbortController();
    controllerRef.current = controller;

    const startStream = async () => {
      try {
        const response = await fetch(
          `${API_BASE}/projects/${projectId}/sessions/${sessionId}/generate-code`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            signal: controller.signal,
          },
        );

        // ACT.A4 (#1703) — 202 means the server queued this generate-code
        // call (per-tenant session load hardening, shared budget with
        // sessions.ts's /chat); it is a plain JSON body, not an SSE stream.
        // MUST be checked before the `!response.ok || !response.body` gate
        // below: 202 is in the `ok` range and does have a body, so without
        // this branch the JSON queue body falls into the SSE-reader loop,
        // is silently absorbed as one incomplete line (no `\n\n` in it),
        // and the stream-end fallback then reports it as a successful EMPTY
        // completion — the bug this block fixes. Mirrors ChatPage.tsx's
        // handleSend 202 handling.
        if (response.status === 202) {
          const queueInfo = await response.json().catch(() => null) as
            { status?: string; position?: number; estimated_wait_seconds?: number } | null;
          const position = queueInfo?.position ?? 1;
          const waitSeconds = queueInfo?.estimated_wait_seconds ?? 1;
          setQueueStatus({ position, waitSeconds });
          const retryDelayMs = Math.max(1000, waitSeconds * 1000);
          queueRetryTimeoutRef.current = window.setTimeout(() => {
            queueRetryTimeoutRef.current = null;
            setQueueStatus(null);
            void startStream();
          }, retryDelayMs);
          return;
        }
        setQueueStatus(null);

        if (!response.ok || !response.body) {
          if (response.status === 402) {
            const body = await response.json().catch(() => ({})) as Record<string, unknown>;
            if (body.error === 'TOKEN_ALLOWANCE_EXHAUSTED') {
              window.dispatchEvent(new CustomEvent('token-exhaustion', { detail: body }));
            }
          }
          setError('Failed to start code generation.');
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let receivedComplete = false;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n\n');
          buffer = lines.pop() ?? '';

          for (const line of lines) {
            const dataLine = line.replace(/^data: /, '');
            if (!dataLine) continue;
            try {
              const data = JSON.parse(dataLine);
              if (data.type === 'token') {
                setContent(prev => {
                  const next = prev + data.content;
                  contentSnapshotRef.current = next;
                  return next;
                });
                setTokenCount(prev => prev + 1);
              }
              if (data.type === 'complete') {
                receivedComplete = true;
                setComplete(true);
                if (data.artefactId) setArtefactId(data.artefactId);
                setUsage(data.usage);
                onCompleteRef.current(data.artefactId ?? null, data.usage);
              }
              if (data.type === 'error') {
                setError(data.error);
              }
            } catch {
              // ignore malformed events (e.g. keepalive comments)
            }
          }
        }

        // If stream ended without explicit complete event but we have content,
        // treat it as complete (server sent res.end() after streaming)
        if (!receivedComplete) {
          setComplete(true);
          onCompleteRef.current(null, { inputTokens: 0, outputTokens: 0 });
        }
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          setError('Connection lost. Please try again.');
        }
      }
    };

    void startStream();

    return () => {
      // ACT.A4 (#1703) — clear any pending queued-retry timer on unmount /
      // dep-change so it never fires (and calls setState / issues a
      // network request) against a component that's gone. Mirrors
      // ChatPage.tsx's dedicated unmount-cleanup effect.
      if (queueRetryTimeoutRef.current !== null) {
        clearTimeout(queueRetryTimeoutRef.current);
        queueRetryTimeoutRef.current = null;
      }
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, sessionId]);

  const handleStop = () => {
    // ACT.A4 (#1703) — a pending queued-retry timer takes priority: by the
    // time it's pending, the original fetch has already completed and
    // controllerRef.current's signal is not tied to anything in flight, so
    // aborting it would be a no-op. Cancel the timer and reset UI state
    // directly instead. Mirrors ChatPage.tsx's handleStop.
    if (queueRetryTimeoutRef.current !== null) {
      clearTimeout(queueRetryTimeoutRef.current);
      queueRetryTimeoutRef.current = null;
      setQueueStatus(null);
      setStopped(true);
      return;
    }
    controllerRef.current?.abort();
    setContent(prev => prev + '\n\n_[Generation stopped by user]_');
    setStopped(true);
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(content);
  };

  const handleExportMarkdown = () => {
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'development-response.md';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Auto-generate summary when streaming completes (fire exactly once)
  useEffect(() => {
    if (!complete || summaryRequestedRef.current) return;
    const snap = contentSnapshotRef.current;
    if (!snap) return;
    summaryRequestedRef.current = true;
    setSummaryLoading(true);
    apiFetch<{ summary: DevResponseSummary }>(
      `/projects/${projectId}/sessions/${sessionId}/generate-summary`,
      { method: 'POST', body: JSON.stringify({ content: snap }) },
    )
      .then(data => setSummary(data.summary))
      .catch(() => { /* summary is optional */ })
      .finally(() => setSummaryLoading(false));
  }, [complete, projectId, sessionId]);

  // Auto-scroll during streaming
  useEffect(() => {
    if (!complete && !stopped && contentRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }, [content, complete, stopped]);

  return (
    <div
      style={{
        borderLeft: '3px solid var(--success)',
        borderRadius: 'var(--radius-sm)',
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderLeftColor: 'var(--success)',
        borderLeftWidth: 3,
        padding: 16,
        marginTop: 8,
        marginBottom: 8,
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 16 }}>&#9889;</span>
          <div>
            <div style={{
              fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600,
              color: 'var(--success)', textTransform: 'uppercase', letterSpacing: '0.05em',
            }}>
              Development Response
            </div>
            <div style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}>
              {complete ? 'Generation complete' : stopped ? 'Generation stopped' : queueStatus ? 'Waiting in queue...' : 'Generating implementation...'}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-secondary)' }}>
            {tokenCount} tokens
          </span>
          {!complete && !stopped && !error && (
            <button
              onClick={handleStop}
              style={{
                fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600,
                padding: '3px 10px', borderRadius: 'var(--radius-sm)',
                border: '1px solid rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.08)',
                color: 'var(--error)', cursor: 'pointer',
              }}
            >
              Stop
            </button>
          )}
        </div>
      </div>

      {error && (
        <div style={{
          background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: 'var(--radius-sm)', padding: '8px 12px', marginBottom: 12,
        }}>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--error)' }}>{error}</p>
        </div>
      )}

      {/* Content */}
      <div
        ref={contentRef}
        style={{
          maxHeight: 500, overflowY: 'auto', marginBottom: 12,
          background: 'var(--bg)', borderRadius: 'var(--radius-sm)', padding: '12px 16px',
        }}
      >
        {content ? (
          <MarkdownRenderer disableMermaid>{content}</MarkdownRenderer>
        ) : queueStatus ? (
          // ACT.A4 (#1703) — per-tenant session queue indicator. Shown
          // while the server has us queued (202) and is about to
          // auto-retry. Mirrors ChatPage.tsx's queue banner, styled to
          // match this component's own (inline-style / CSS-var) convention.
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
            background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.25)',
            borderRadius: 'var(--radius-sm)',
          }}>
            <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--warning)' }}>
              Server is busy — you are #{queueStatus.position} in line, retrying in ~{queueStatus.waitSeconds}s...
            </span>
          </div>
        ) : !error ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 12 }}>
            <div style={{
              width: 14, height: 14, borderRadius: '50%',
              border: '2px solid var(--primary)', borderTopColor: 'transparent',
              animation: 'spin 1s linear infinite',
            }} />
            <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}>
              Waiting for response...
            </span>
          </div>
        ) : null}
      </div>

      {/* Completion footer */}
      {(complete || stopped) && (
        <div style={{
          borderTop: '1px solid var(--border)', paddingTop: 12,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8,
        }}>
          {usage && (
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-secondary)' }}>
              Tokens used: {usage.inputTokens} in / {usage.outputTokens} out
            </span>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            {artefactId && (
              <a
                href={`/artefacts?tabs=${artefactId}&active=${artefactId}`}
                style={{
                  fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600,
                  color: 'var(--primary)', textDecoration: 'none',
                }}
              >
                View Full Response &rarr;
              </a>
            )}
            <button
              onClick={handleExportMarkdown}
              style={{
                fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 500,
                padding: '4px 10px', borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border)', background: 'transparent',
                color: 'var(--text)', cursor: 'pointer',
              }}
            >
              Export as Markdown
            </button>
            <button
              onClick={() => void handleCopy()}
              style={{
                fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 500,
                padding: '4px 10px', borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border)', background: 'transparent',
                color: 'var(--text)', cursor: 'pointer',
              }}
            >
              Copy All
            </button>
          </div>
        </div>
      )}

      {/* Summary card */}
      {summaryLoading && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, marginTop: 12,
          padding: '10px 14px', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
        }}>
          <div style={{
            width: 12, height: 12, borderRadius: '50%',
            border: '2px solid var(--primary)', borderTopColor: 'transparent',
            animation: 'spin 1s linear infinite',
          }} />
          <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>
            Generating implementation summary...
          </span>
        </div>
      )}
      {summary && (
        <SummaryCard
          summary={summary}
          issueId={issueId}
          artefactId={artefactId}
        />
      )}
    </div>
  );
}
